import type { GameState } from "@/types/game";
import { getPassive } from "./skills/registry";
import { stripAllEquipment } from "./Equipment";

// 濒死规则：hp<=0 进入濒死；转一圈到濒死玩家自己的下一回合开始时结算（死亡/重生）
// 队友在此期间可用补给/复活药水救援使其脱险

// 伤害结算后标记濒死
export function markDying(state: GameState, playerId: string): GameState {
  const target = state.players.find((p) => p.id === playerId);
  if (!target || target.hp > 0 || target.isDying) return state;
  const players = state.players.map((p) =>
    p.id === playerId ? { ...p, isDying: true } : p
  );
  return {
    ...state,
    players,
    pendingDyingPlayers: state.pendingDyingPlayers.includes(playerId)
      ? state.pendingDyingPlayers
      : [...state.pendingDyingPlayers, playerId],
    log: [...state.log, { type: "dying", playerId }],
  };
}

// 补给恢复使濒死玩家脱险
export function rescueByHeal(state: GameState, playerId: string): GameState {
  const target = state.players.find((p) => p.id === playerId);
  if (!target || target.hp <= 0) return state;
  const players = state.players.map((p) =>
    p.id === playerId ? { ...p, isDying: false } : p
  );
  return {
    ...state,
    players,
    pendingDyingPlayers: state.pendingDyingPlayers.filter((id) => id !== playerId),
    log: [
      ...state.log,
      { type: "rescue", playerId, byPlayerId: playerId, cardId: "" },
    ],
  };
}

// 单个濒死玩家结算：转一圈到该玩家回合开始时调用
// hp > 0 → 脱险（兜底，正常 rescueByHeal 已清 isDying）；hp <= 0 → 重生或阵亡
export function finalizeDyingPlayer(
  state: GameState,
  playerId: string
): GameState {
  const target = state.players.find((p) => p.id === playerId);
  if (!target || !target.isDying) return state;
  const log = [...state.log];
  // 已恢复（hp > 0）：脱险
  if (target.hp > 0) {
    return {
      ...state,
      players: state.players.map((p) =>
        p.id === playerId ? { ...p, isDying: false } : p
      ),
      pendingDyingPlayers: state.pendingDyingPlayers.filter(
        (id) => id !== playerId
      ),
    };
  }
  // 卸下全部装备并回收生命上限加成（魔血戒指），收集手牌+装备待弃
  const stripped = stripAllEquipment(target);
  const cardsToDiscard = [...target.hand, ...stripped.cards];
  // 圣殿骑士重生：死亡确定后弃全部 + 恢复至 40hp，全局 1 次
  const passive = getPassive(target.hero.name);
  let players = state.players;
  if (passive.revivalHp !== undefined && !target.revivalUsed) {
    players = players.map((x) =>
      x.id === playerId
        ? {
            ...stripped.player,
            isDying: false,
            revivalUsed: true,
            hp: Math.min(passive.revivalHp!, stripped.player.maxHp),
            hand: [],
          }
        : x
    );
    log.push({ type: "revive", playerId, amount: passive.revivalHp! });
    if (cardsToDiscard.length > 0) {
      log.push({ type: "discard", playerId, count: cardsToDiscard.length });
    }
  } else {
    // 阵亡：手牌+装备全部弃去
    players = players.map((x) =>
      x.id === playerId
        ? {
            ...stripped.player,
            isAlive: false,
            isDying: false,
            hp: 0,
            hand: [],
          }
        : x
    );
    log.push({ type: "death", playerId });
    if (cardsToDiscard.length > 0) {
      log.push({ type: "discard", playerId, count: cardsToDiscard.length });
    }
  }
  let next: GameState = {
    ...state,
    players,
    pendingDyingPlayers: state.pendingDyingPlayers.filter(
      (id) => id !== playerId
    ),
    log,
  };
  // 胜负判定：一方全灭则游戏结束
  const teamAAlive = next.players.some((p) => p.team === "A" && p.isAlive);
  const teamBAlive = next.players.some((p) => p.team === "B" && p.isAlive);
  if (!teamAAlive || !teamBAlive) {
    const winners: ("A" | "B")[] = teamAAlive ? ["A"] : teamBAlive ? ["B"] : [];
    next = {
      ...next,
      phase: "game-over",
      log: [...next.log, { type: "game-over", winners }],
    };
  }
  return next;
}

// 一圈结束（endTurn 环绕）：清理圈结束到期的状态（剧毒）+ 兜底胜负判定
// 濒死玩家已在自己回合开始时由 finalizeDyingPlayer 结算，此处不再处理濒死
export function finalizeCircleEnd(state: GameState): GameState {
  // 清除本圈结束到期的状态（剧毒）
  const players = state.players.map((p) =>
    p.statuses.some((s) => s.expiresOnCircleEnd)
      ? { ...p, statuses: p.statuses.filter((s) => !s.expiresOnCircleEnd) }
      : p
  );

  let nextState: GameState = {
    ...state,
    players,
    // 兜底：清理残留的濒死标记（理论上 finalizeDyingPlayer 已清空）
    pendingDyingPlayers: [],
  };

  // 胜负判定：一方全灭
  const teamAAlive = nextState.players.some((p) => p.team === "A" && p.isAlive);
  const teamBAlive = nextState.players.some((p) => p.team === "B" && p.isAlive);
  if (!teamAAlive || !teamBAlive) {
    const winners: ("A" | "B")[] = teamAAlive ? ["A"] : teamBAlive ? ["B"] : [];
    nextState = {
      ...nextState,
      phase: "game-over",
      log: [...nextState.log, { type: "game-over", winners }],
    };
  }
  return nextState;
}

// 游戏是否已结束
export function isGameOver(state: GameState): boolean {
  return state.phase === "game-over";
}
